import { UserOutlined } from "@ant-design/icons"
import {
  App,
  Avatar,
  Button,
  Divider,
  Flex,
  Popover,
  Tooltip,
  Typography
} from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"
import { type NavigateFunction } from "react-router/dist/lib/hooks"

import Icon from "../../Icon"
import type { AppDispatch, RootState } from "../../store.ts"
import { logout } from "../LoginPage/redux/auth.thunks.ts"
import { fetchUserInfo } from "./redux/user.thunks"

const { Text } = Typography

const NavUserMenu = ({
  user,
  logout,
  isSidebarOpen,
  getUserInfo
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const role = user?.role?.name || "User"

  const name = useMemo(() => {
    const fullName = [user?.first_name, user?.middle_name, user?.last_name]
      ?.filter(Boolean)
      ?.join(" ")

    return fullName || "No name"
  }, [user])

  const handleLogout = () => {
    modal.confirm({
      title: t("logout.confirmTitle"),
      content: t("logout.confirmBody"),
      okText: t("logout.confirmOk"),
      cancelText: t("logout.confirmCancel"),
      onOk: () => {
        logout(navigate)
      }
    })
  }

  useEffect(() => {
    if (!!user && !location?.pathname?.includes("account")) {
      getUserInfo()
    }
  }, [])

  const userInfo = (
    <Flex gap={8} align="center" className="overflow-hidden">
      <Avatar className="shrink-0" src={user?.avatar} icon={<UserOutlined />} />
      <Flex vertical className="overflow-hidden">
        <Text ellipsis={{ tooltip: name }} strong>
          {name}
        </Text>
        <Text ellipsis={{ tooltip: role }} className="text-xs" type="secondary">
          {role}
        </Text>
      </Flex>
    </Flex>
  )

  if (!isSidebarOpen) {
    return (
      <Popover
        content={
          <Flex vertical>
            {userInfo}
            <Divider className="my-2" />

            <Button
              onClick={handleLogout}
              iconPosition={"end"}
              icon={<Icon type="Logout" />}
            >
              {t("Logout")}
            </Button>
          </Flex>
        }
        trigger="hover"
        placement="rightTop"
        arrow={false}
      >
        <Flex className="p-2.5 cursor-pointer" justify="center" align="center">
          <Avatar
            size="small"
            className="shrink-0"
            src={user?.avatar}
            icon={<UserOutlined />}
          />
        </Flex>
      </Popover>
    )
  }

  return (
    <Flex className="p-2.5" justify="space-between" align="center">
      {userInfo}
      <Tooltip title={t("Logout")}>
        <Button
          onClick={handleLogout}
          variant={"text"}
          type={"text"}
          icon={<Icon type={"Logout"} />}
        />
      </Tooltip>
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.userInfo,
  isSidebarOpen: state.nav.isSidebarOpen
})

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getUserInfo: () => dispatch(fetchUserInfo()),
  logout: (navigate: NavigateFunction) => dispatch(logout({ navigate }))
})

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(NavUserMenu)
